import {
  Component,
  computed,
  ElementRef,
  inject,
  input,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { NovaButton } from '@rds-nova/angular-button';
import {
  NovaDropdown,
  NovaDropdownContent,
  NovaDropdownItem,
  NovaDropdownTrigger,
} from '@rds-nova/angular-dropdown';
import { NovaIcon, NovaIconRegistry } from '@rds-nova/angular-icon';
import { filter } from 'rxjs';

import { SUPPORT_PHONE_NUMBER } from 'src/app/core/constants/support';
import { LanguageService } from 'src/app/core/services/language.service';
import { AppFooter } from 'src/app/features/shared/footer/footer';
import { CompactFooter } from 'src/app/features/shared/footer/compact-footer';

@Component({
  selector: 'app-shell',
  imports: [
    TranslatePipe,
    AppFooter,
    CompactFooter,
    NovaButton,
    NovaIcon,
    NovaIconRegistry,
    NovaDropdown,
    NovaDropdownTrigger,
    NovaDropdownContent,
    NovaDropdownItem,
  ],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
})
export class AppShell {
  readonly title = input('');
  protected readonly phoneNumber = SUPPORT_PHONE_NUMBER;

  @ViewChildren('langItem') private langItems!: QueryList<ElementRef>;

  protected readonly languageService = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  private readonly navigationEnd = toSignal(
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd))
  );

  private readonly footerType = computed(() => {
    this.navigationEnd();
    let active = this.route;
    while (active.firstChild) {
      active = active.firstChild;
    }
    return (active.snapshot.data['footerType'] as string) ?? 'full';
  });

  protected readonly useCompactFooter = computed(() => this.footerType() === 'compact');
  protected readonly hideFooter = computed(() => this.footerType() === 'none');

  protected selectLanguage(code: string): void {
    this.languageService.setLanguage(code);
  }

  protected onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const items = this.langItems?.toArray();
      if (items?.length) {
        items[0].nativeElement.focus();
      }
    }
  }

  protected onItemKeydown(event: KeyboardEvent, code: string): void {
    const items = this.langItems?.toArray();
    if (!items?.length) return;

    const currentIndex = items.findIndex(
      (item) => item.nativeElement === event.target
    );

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        items[(currentIndex + 1) % items.length].nativeElement.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        items[(currentIndex - 1 + items.length) % items.length].nativeElement.focus();
        break;
      case 'Home':
        event.preventDefault();
        items[0].nativeElement.focus();
        break;
      case 'End':
        event.preventDefault();
        items[items.length - 1].nativeElement.focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectLanguage(code);
        break;
      case 'Tab':
        this.selectLanguage(code);
        break;
    }
  }
}
